import { useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MaterialIcon } from '@/components/common/MaterialIcon';
import { Logo } from '@/components/common/Logo';
import { ScrollToTop } from '@/components/common/ScrollToTop';
import { useAuth } from '@/contexts/AuthContext';
import { adminContactService } from '@/features/admin/adminService';
import { cn } from '@/lib/cn';

/**
 * Sidebar shell for the Admin Dashboard. Mounted in the router as a layout
 * route under {@code /admin}; child routes render inside {@link Outlet}.
 *
 * Layout:
 *   • lg+    persistent sidebar on the left, content on the right
 *   • md     same, narrower sidebar (icons + labels)
 *   • sm     top app-bar with horizontal scroll nav, no sidebar
 *
 * Auth: role check happens upstream in {@code ProtectedRoute}; this layout
 * assumes an admin viewer is present.
 */
export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Lightweight unread-badge query — same cache key as the messages page
  // so they share state without an extra fetch.
  const { data: unread = 0 } = useQuery({
    queryKey: ['admin', 'contact', 'unread-count'],
    queryFn: () => adminContactService.unreadCount(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const items = useMemo(
    () => [
      { to: '/admin/dashboard', icon: 'dashboard', label: 'Dashboard' },
      { to: '/admin/members', icon: 'group', label: 'Members' },
      { to: '/admin/messages', icon: 'mail', label: 'Contact Messages', badge: unread },
    ],
    [unread],
  );

  const handleSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#fff9eb]">
      <ScrollToTop />
      <div className="mx-auto flex max-w-[1500px] flex-col gap-0 md:flex-row">
        {/* Mobile app bar (visible <md) */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[rgba(31,27,29,0.08)] bg-white px-4 py-3 md:hidden">
          <Logo size={36} />
          <span className="font-headline-sm text-base font-bold italic text-[rgba(31,27,29,0.85)]">
            Admin
          </span>
          <nav aria-label="Admin sections (mobile)" className="ml-auto flex gap-1 overflow-x-auto">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold',
                    isActive
                      ? 'bg-brand-teal text-white'
                      : 'text-on-surface-variant hover:bg-[#fff9eb] hover:text-brand-teal',
                  )
                }
              >
                <MaterialIcon name={it.icon} className="text-sm" />
                {it.label}
                {!!it.badge && it.badge > 0 && (
                  <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-on-error">
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </header>

        {/* Sidebar (md+) */}
        <aside
          aria-label="Admin navigation"
          className="hidden shrink-0 border-r border-[rgba(31,27,29,0.08)] bg-white md:flex md:w-60 md:flex-col lg:w-64"
        >
          <div className="flex items-center gap-3 border-b border-[rgba(31,27,29,0.08)] px-5 py-5">
            <Logo size={40} />
            <div>
              <p className="font-label-caps text-[10px] uppercase tracking-widest text-brand-teal">
                MCCWA
              </p>
              <h2 className="font-headline-sm text-base font-bold italic text-[rgba(31,27,29,0.85)]">
                Admin
              </h2>
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-5">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-xl px-3 py-2.5 font-body-md text-body-md transition-colors',
                    isActive
                      ? 'bg-brand-teal text-white shadow-sm'
                      : 'text-on-surface hover:bg-[#fff9eb] hover:text-brand-teal',
                  )
                }
              >
                <MaterialIcon name={it.icon} />
                <span className="flex-1">{it.label}</span>
                {!!it.badge && it.badge > 0 && (
                  <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-error px-1.5 text-[10px] font-bold text-on-error">
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-[rgba(31,27,29,0.08)] p-4">
            {user && (
              <div className="mb-3 rounded-xl bg-[#fff9eb] p-3">
                <p className="truncate font-body-md text-sm font-semibold text-on-surface">
                  {user.firstName} {user.lastName}
                </p>
                <p className="truncate text-xs text-on-surface-variant">{user.email}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {user.roles.map((r) => (
                    <span
                      key={r}
                      className="rounded-full bg-brand-teal/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-teal"
                    >
                      {r.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 font-body-md text-body-md text-on-surface-variant transition-colors hover:bg-error-container/40 hover:text-on-error-container"
            >
              <MaterialIcon name="logout" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1 px-4 py-8 md:px-8 md:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
